import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { productsAPI, favoritesAPI, qrAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, Heart } from '@phosphor-icons/react';
import QRCodeCard from '../components/QRCodeCard';
import ReviewsSection, { ReviewsSummary } from '../components/ReviewsSection';
import { toast } from 'sonner';

import { ProductImageGallery } from '../components/product-detail/ProductImageGallery';
import { ListingTypeBadge } from '../components/product-detail/ListingTypeBadge';
import { CrowdfundSection } from '../components/product-detail/CrowdfundSection';
import { StandardBuySection } from '../components/product-detail/StandardBuySection';
import { OfferSection } from '../components/product-detail/OfferSection';
import { TipJarSection } from '../components/product-detail/TipJarSection';
import { SubscriptionSection } from '../components/product-detail/SubscriptionSection';
import { InvestmentSection } from '../components/product-detail/InvestmentSection';
import { SellerInfoCard } from '../components/product-detail/SellerInfoCard';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);

  const loadProduct = useCallback(async () => {
    try {
      const response = await productsAPI.get(productId);
      setProduct(response.data.product);
      setSeller(response.data.seller);
      qrAPI.recordScan('product', productId).catch((err) => console.error('QR scan tracking failed:', err));
    } catch (error) {
      toast.error(formatApiError(error));
      navigate('/explore');
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  const checkFavorite = useCallback(async () => {
    try {
      const response = await favoritesAPI.list();
      const fav = response.data.find(f => f.type === 'product' && f.target_id === productId);
      if (fav) {
        setIsFavorited(true);
        setFavoriteId(fav.favorite_id);
      }
    } catch { /* silent */ }
  }, [productId]);

  useEffect(() => {
    loadProduct();
    if (isAuthenticated) checkFavorite();
  }, [loadProduct, checkFavorite, isAuthenticated]);

  const handleFavorite = async () => {
    if (!isAuthenticated) { toast.error('Please login to add favorites'); return; }
    try {
      if (isFavorited) {
        await favoritesAPI.remove(favoriteId);
        setIsFavorited(false);
        setFavoriteId(null);
        toast.success('Removed from favorites');
      } else {
        const response = await favoritesAPI.favoriteProduct(productId);
        setIsFavorited(true);
        setFavoriteId(response.data.favorite_id);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCrowdfundUpdate = (update) => {
    setProduct(prev => ({ ...prev, ...update }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-[#999999]">Loading...</div>
      </div>
    );
  }

  if (!product) return null;

  const isOwnProduct = user?.user_id === product.user_id;
  const listingType = product.listing_type;

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Link to="/explore" className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-6" data-testid="back-to-explore">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Explore
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          <div>
            <ProductImageGallery product={product} />
          </div>

          <div>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xs bg-[#1A1A1A] px-3 py-1 rounded-sm text-[#999999] font-medium">
                  {product.category}
                </span>
                <ListingTypeBadge product={product} />
              </div>
              <Button
                variant="ghost"
                onClick={handleFavorite}
                className={isFavorited ? 'text-[#FF3B30]' : 'text-[#999999]'}
                data-testid="favorite-button"
              >
                {isFavorited ? <Heart className="w-6 h-6" weight="fill" /> : <Heart className="w-6 h-6" />}
              </Button>
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="product-name">
              {product.name}
            </h1>

            {(product.avg_rating > 0 || product.review_count > 0) && (
              <div className="mb-3">
                <ReviewsSummary avgRating={product.avg_rating} totalReviews={product.review_count} />
              </div>
            )}

            <div
              className="text-[#999999] leading-relaxed mb-6 prose prose-invert prose-sm max-w-none [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_strong]:text-white [&_blockquote]:border-l-[#22C55E] [&_blockquote]:text-[#999999] [&_li]:text-[#999999]"
              data-testid="product-description"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description || '') }}
            />

            {product.is_crowdfund ? (
              <CrowdfundSection
                product={product}
                productId={productId}
                isAuthenticated={isAuthenticated}
                isOwnProduct={isOwnProduct}
                formatCurrency={formatCurrency}
                onContribute={handleCrowdfundUpdate}
                navigate={navigate}
              />
            ) : (
              <StandardBuySection
                product={product}
                productId={productId}
                isAuthenticated={isAuthenticated}
                isOwnProduct={isOwnProduct}
                formatCurrency={formatCurrency}
                navigate={navigate}
              />
            )}

            {listingType === 'offer' && !isOwnProduct && product.available && (
              <OfferSection
                productId={productId}
                isAuthenticated={isAuthenticated}
                formatCurrency={formatCurrency}
                navigate={navigate}
              />
            )}

            {listingType === 'tip_jar' && !isOwnProduct && product.available && (
              <TipJarSection
                product={product}
                productId={productId}
                isAuthenticated={isAuthenticated}
                formatCurrency={formatCurrency}
                navigate={navigate}
              />
            )}

            {listingType === 'subscription' && !isOwnProduct && product.available && (
              <SubscriptionSection
                product={product}
                productId={productId}
                isAuthenticated={isAuthenticated}
                formatCurrency={formatCurrency}
                navigate={navigate}
              />
            )}

            {isOwnProduct && (
              <Link to={`/products/edit/${product.product_id}`}>
                <Button variant="outline" className="w-full rounded-sm h-12 border-[#333333] hover:bg-[#333333] text-white" data-testid="edit-product-button">
                  Edit Product
                </Button>
              </Link>
            )}

            {product.investment_enabled && !product.is_crowdfund && (
              <InvestmentSection
                product={product}
                productId={productId}
                isAuthenticated={isAuthenticated}
                isOwnProduct={isOwnProduct}
                formatCurrency={formatCurrency}
                navigate={navigate}
              />
            )}

            <SellerInfoCard seller={seller} />

            <div className="mt-4">
              <QRCodeCard
                type="product"
                targetId={productId}
                title={`QR: ${product?.name}`}
                subtitle="Scan to view this product"
                showStats={isAuthenticated && product?.user_id === user?.user_id}
              />
            </div>

            <ReviewsSection productId={productId} sellerId={product?.user_id} />
          </div>
        </div>
      </main>
    </div>
  );
}
